import React, { useState, ReactNode } from "react";
import "./BaseForm.css";

export interface FormField {
  name: string;
  label: string;
  type: "text" | "email" | "password" | "tel" | "textarea";
  placeholder?: string;
  required?: boolean;
  validation?: (value: string) => string | null;
  value?: string;
  onChange?: (value: string) => void;
}

export interface BaseFormProps {
  title: string;
  subtitle?: string;
  fields: FormField[];
  onSubmit: (formData: Record<string, string>) => Promise<void>;
  submitButtonText: string;
  loading?: boolean;
  error?: string;
  success?: string;
  showHeader?: boolean;
  className?: string;
  children?: ReactNode;
  onLoadingChange?: (loading: boolean) => void;
}

export const BaseForm: React.FC<BaseFormProps> = ({
  title,
  subtitle,
  fields,
  onSubmit,
  submitButtonText,
  loading: externalLoading = false,
  error: externalError,
  success: externalSuccess,
  showHeader = true,
  className = "",
  children,
  onLoadingChange,
}) => {
  // Initialize form state from fields
  const initialFormData = fields.reduce(
    (acc, field) => {
      acc[field.name] = field.value || "";
      return acc;
    },
    {} as Record<string, string>,
  );

  const [formData, setFormData] =
    useState<Record<string, string>>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState("");
  const [internalSuccess, setInternalSuccess] = useState("");

  const loading = externalLoading || internalLoading;
  const error = externalError || internalError;
  const success = externalSuccess || internalSuccess;

  // Sync form data when field *values* actually change.
  // Uses a serialised fingerprint so a new `fields` array reference with the
  // same logical content does NOT trigger a state update (prevents re-render
  // loops when callers create the fields array inline every render).
  const fieldsFingerprint = fields
    .map((f) => `${f.name}:${f.value ?? ""}`)
    .join("|");
  React.useEffect(() => {
    setFormData((prev) => {
      const next: Record<string, string> = {};
      let changed = false;
      for (const field of fields) {
        const val = field.value || prev[field.name] || "";
        next[field.name] = val;
        if (val !== prev[field.name]) changed = true;
      }
      return changed ? next : prev;
    });
  }, [fieldsFingerprint]);

  const handleFieldChange = (fieldName: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));

    // Clear field-specific error when user starts typing
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }

    // Clear internal error/success when user makes changes
    if (internalError) setInternalError("");
    if (internalSuccess) setInternalSuccess("");
  };

  const validateField = (field: FormField, value: string): string | null => {
    // Required field validation
    if (field.required && !value.trim()) {
      return `${field.label} is required`;
    }

    // Email validation
    if (field.type === "email" && value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value.trim())) {
        return "Please enter a valid email address";
      }
    }

    // Password validation
    if (field.type === "password" && value.trim()) {
      if (value.length < 8) {
        return "Password must be at least 8 characters long";
      }
    }

    // Custom validation
    if (field.validation) {
      return field.validation(value);
    }

    return null;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    fields.forEach((field) => {
      const value = formData[field.name] || "";
      const error = validateField(field, value);
      if (error) {
        newErrors[field.name] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setInternalLoading(true);
    setInternalError("");
    setInternalSuccess("");
    onLoadingChange?.(true);

    try {
      await onSubmit(formData);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An error occurred";
      setInternalError(errorMessage);
    } finally {
      setInternalLoading(false);
      onLoadingChange?.(false);
    }
  };

  const renderField = (field: FormField) => {
    const fieldError = errors[field.name];
    const fieldValue = formData[field.name] || "";

    return (
      <div key={field.name} className="form-group">
        <label htmlFor={field.name}>
          {field.label}
          {field.required && <span className="required">*</span>}
        </label>

        {field.type === "textarea" ? (
          <textarea
            id={field.name}
            name={field.name}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={`form-input ${fieldError ? "error" : ""}`}
            rows={4}
            disabled={loading}
          />
        ) : (
          <input
            type={field.type}
            id={field.name}
            name={field.name}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={`form-input ${fieldError ? "error" : ""}`}
            disabled={loading}
            autoComplete={(() => {
              if (field.type === "password") return "new-password";
              if (field.type === "email") return "email";
              return "off";
            })()}
          />
        )}

        {fieldError && <div className="field-error">{fieldError}</div>}
      </div>
    );
  };

  return (
    <div className={`base-form-container ${className}`}>
      {showHeader && (
        <div className="form-header">
          <h2 className="form-title">{title}</h2>
          {subtitle && <p className="form-subtitle">{subtitle}</p>}
        </div>
      )}

      <form onSubmit={handleSubmit} className="base-form">
        {error && <div className="error-message">{error}</div>}

        {success && <div className="success-message">{success}</div>}

        <div className="form-fields">{fields.map(renderField)}</div>

        {children && <div className="form-children">{children}</div>}

        <button
          type="submit"
          className={`submit-button ${loading ? "loading" : ""}`}
          disabled={loading}
        >
          {loading ? (
            <div className="button-loading">
              <div className="spinner"></div>
              <span>Processing...</span>
            </div>
          ) : (
            submitButtonText
          )}
        </button>
      </form>
    </div>
  );
};

// Utility functions for common form operations
export const createEmailField = (
  name: string = "email",
  label: string = "Email",
  required: boolean = true,
  placeholder: string = "Enter your email",
): FormField => ({
  name,
  label,
  type: "email",
  placeholder,
  required,
  validation: (value: string) => {
    if (!value.trim()) return required ? "Email is required" : null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value.trim())
      ? null
      : "Please enter a valid email address";
  },
});

export const createPasswordField = (
  name: string = "password",
  label: string = "Password",
  required: boolean = true,
  placeholder: string = "Enter your password",
): FormField => ({
  name,
  label,
  type: "password",
  placeholder,
  required,
  validation: (value: string) => {
    if (!value.trim()) return required ? "Password is required" : null;
    if (value.length < 8) return "Password must be at least 8 characters long";
    return null;
  },
});

export const createTextField = (
  name: string,
  label: string,
  required: boolean = true,
  placeholder?: string,
): FormField => ({
  name,
  label,
  type: "text",
  placeholder,
  required,
  validation: (value: string) => {
    if (!value.trim()) return required ? `${label} is required` : null;
    return null;
  },
});

export const createConfirmPasswordField = (
  passwordFieldName: string = "password",
  name: string = "confirmPassword",
  label: string = "Confirm Password",
  required: boolean = true,
  placeholder: string = "Confirm your password",
): FormField => ({
  name,
  label,
  type: "password",
  placeholder,
  required,
  validation: (value: string, formData?: Record<string, string>) => {
    if (!value.trim())
      return required ? "Password confirmation is required" : null;
    if (formData && value !== formData[passwordFieldName]) {
      return "Passwords do not match";
    }
    return null;
  },
});

// Hook for form state management
export const useFormState = (initialData: Record<string, string> = {}) => {
  const [formData, setFormData] = useState<Record<string, string>>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const updateField = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const setError = (name: string, error: string) => {
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const clearErrors = () => {
    setErrors({});
  };

  const resetForm = () => {
    setFormData(initialData);
    setErrors({});
    setLoading(false);
  };

  return {
    formData,
    errors,
    loading,
    updateField,
    setError,
    clearErrors,
    resetForm,
    setLoading,
  };
};
